// ============================================================
// Income Dashboard Page (/income/dashboard)
// ============================================================

import { useEffect, useState, useMemo } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Card, CardContent, TextField, Chip, Alert, Grid } from '@mui/material';
import { Banknote, TrendingUp, TrendingDown, ArrowUpRight, Tag, Calendar, Award, BarChart3, Layers, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { Loader } from '@/components/feedback/Loader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useSchool } from '@/hooks/useSchool';
import { useIncomes, useIncomeCategories } from '@/hooks/useIncomes';
import { useNavigate } from 'react-router-dom';
import { tokens } from '@/theme/tokens';
import toast from 'react-hot-toast';

const CATEGORY_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#64748B'];
const CHART_COLOR = '#10B981'; // Green for Income

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length < 3) return dateStr;
  const [year, month, day] = parts;
  const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateToISO = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DATE_PRESET_OPTIONS = [
  { value: 'thisMonth', label: 'This Month' },
  { value: 'today', label: 'Today' },
  { value: '7days', label: '7 Days' },
  { value: '15days', label: '15 Days' },
  { value: '1month', label: 'One Month' },
  { value: '2months', label: '2 Months' },
  { value: 'custom', label: 'Custom' },
];

const getPresetDates = (preset) => {
  const now = new Date();
  const todayStr = formatDateToISO(now);

  switch (preset) {
    case 'today':
      return { from: todayStr, to: todayStr };
    case '7days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 6);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '15days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 14);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case 'thisMonth': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '1month': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 1);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '2months': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 2);
      return { from: formatDateToISO(d), to: todayStr };
    }
    default:
      return null;
  }
};

function DashboardStatCard({ title, value, subtext, subtextColor, icon: Icon, color = 'success', onClick }) {
  const colorMap = {
    primary: tokens.colors.primary.main,
    success: tokens.colors.success.main,
    warning: tokens.colors.warning.main,
    error: tokens.colors.error.main,
    info: tokens.colors.info.main,
    secondary: '#EC4899',
    purple: '#8B5CF6',
  };
  const themeColor = colorMap[color] || (typeof color === 'string' && color.startsWith('#') ? color : tokens.colors.success.main);

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        minHeight: 112,
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        border: `1px solid ${tokens.colors.border}`,
        boxShadow: tokens.shadows.card,
        '&:hover': {
          boxShadow: tokens.shadows.md,
          transform: onClick ? 'translateY(-2px)' : 'none',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: tokens.colors.text.muted,
                mb: 0.5,
                fontWeight: 500,
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: tokens.colors.text.primary,
                lineHeight: 1.2,
                fontSize: '1.45rem',
                mb: 0.5,
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: subtextColor || tokens.colors.text.muted,
                fontWeight: subtextColor ? 600 : 500,
                fontSize: '0.75rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.2,
              }}
            >
              {subtext || '\u00A0'}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: tokens.radius.md,
              backgroundColor: `${themeColor}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: 1.5,
            }}
          >
            <Icon size={22} color={themeColor} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const firstData = payload[0]?.payload;
  const title = firstData?.category_name || firstData?.dateLabel || label;

  return (
    <Box
      sx={{
        backgroundColor: tokens.colors.card, p: 1.5,
        borderRadius: tokens.radius.sm, boxShadow: tokens.shadows.md,
        border: `1px solid ${tokens.colors.border}`,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: tokens.colors.text.primary }}>
        {title}
      </Typography>
      {payload.map((item, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', color: item.color || tokens.colors.text.secondary }}>
          {item.name}: <strong style={{ color: tokens.colors.text.primary }}>{formatCurrency(item.value)}</strong>
        </Typography>
      ))}
    </Box>
  );
}

const BarLabel = ({ x, y, width, value }) => (
  <text x={x + width / 2} y={y - 6} fill={tokens.colors.text.secondary} textAnchor="middle" fontSize={10} fontWeight={600}>
    {formatCurrency(value)}
  </text>
);

const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);

  if (percent < 0.04) return null;
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const TABLE_SX = {
  width: '100%', borderCollapse: 'collapse',
  '& th': {
    py: 1.25, px: 1.5, textAlign: 'left', fontSize: '0.75rem', fontWeight: 600,
    color: tokens.colors.text.muted, borderBottom: `1px solid ${tokens.colors.border}`,
    whiteSpace: 'nowrap',
  },
  '& td': {
    py: 1.25, px: 1.5, fontSize: '0.8125rem', color: tokens.colors.text.primary,
    borderBottom: `1px solid ${tokens.colors.border}`, whiteSpace: 'nowrap',
  },
  '& tbody tr:hover': { backgroundColor: tokens.colors.grey[50], cursor: 'pointer' },
  '& tbody tr:last-child td': { borderBottom: 'none' },
};

export function IncomeDashboardPage() {
  usePageMeta('Income Dashboard', [
    { label: 'Incomes', path: '/income' },
    { label: 'Dashboard' },
  ]);

  const navigate = useNavigate();
  const { schoolId } = useSchool();

  // Filters state
  const [selectedCategoryId, setSelectedCategoryId] = useState('ALL');
  const [datePreset, setDatePreset] = useState('thisMonth');
  const [fromDate, setFromDate] = useState(() => getPresetDates('thisMonth')?.from || '');
  const [toDate, setToDate] = useState(() => getPresetDates('thisMonth')?.to || '');

  // Queries
  const { data: categories = [] } = useIncomeCategories(true);
  const { data: incomes = [], isLoading, error, refetch } = useIncomes({
    start_date: fromDate || undefined,
    end_date: toDate || undefined,
  });

  const handlePresetChange = (e) => {
    const val = e.target.value;
    setDatePreset(val);
    if (val !== 'custom') {
      const range = getPresetDates(val);
      if (range) {
        setFromDate(range.from);
        setToDate(range.to);
      }
    }
  };

  // Filter incomes by category if selected
  const filteredIncomes = useMemo(() => {
    if (selectedCategoryId === 'ALL') return incomes;
    return incomes.filter((inc) => (inc.category_id || inc.category?.id) === selectedCategoryId);
  }, [incomes, selectedCategoryId]);

  // Compute stats on filtered incomes
  const stats = useMemo(() => {
    const total = filteredIncomes.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const count = filteredIncomes.length;
    const avg = count > 0 ? total / count : 0;
    const largest = filteredIncomes.reduce((max, curr) => Math.max(max, Number(curr.amount || 0)), 0);

    // Group by category
    const catMap = {};
    filteredIncomes.forEach((inc) => {
      const catName = inc.category?.name || 'Uncategorized';
      const catId = inc.category_id || inc.category?.id || 'UNCATEGORIZED';
      if (!catMap[catId]) {
        catMap[catId] = { category_id: catId, category_name: catName, total_amount: 0, expense_count: 0 };
      }
      catMap[catId].total_amount += Number(inc.amount || 0);
      catMap[catId].expense_count += 1;
    });

    const categoryBreakdown = Object.values(catMap)
      .sort((a, b) => b.total_amount - a.total_amount)
      .map((c) => ({
        ...c,
        percentage_of_total: total > 0 ? Number(((c.total_amount / total) * 100).toFixed(1)) : 0,
      }));

    // Group by date for Daily Trend
    const dateMap = {};
    filteredIncomes.forEach((inc) => {
      const d = inc.income_date;
      if (!dateMap[d]) dateMap[d] = 0;
      dateMap[d] += Number(inc.amount || 0);
    });

    const dailyTrend = Object.keys(dateMap)
      .sort()
      .map((d) => ({
        date: d,
        dateLabel: formatDate(d),
        total_amount: dateMap[d],
      }));

    // Recent incomes
    const recent = [...filteredIncomes]
      .sort((a, b) => new Date(b.income_date) - new Date(a.income_date))
      .slice(0, 5)
      .map((inc) => ({
        ...inc,
        category_name: inc.category?.name || 'Uncategorized',
      }));

    return {
      total,
      count,
      avg,
      largest,
      categoryBreakdown,
      dailyTrend,
      recent,
    };
  }, [filteredIncomes]);

  // Formatted donut data
  const pieChartData = useMemo(() => {
    return stats.categoryBreakdown.map((c, i) => ({
      name: c.category_name,
      value: c.total_amount,
      percentage: c.percentage_of_total,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }));
  }, [stats.categoryBreakdown]);

  const topCategories = useMemo(() => {
    return stats.categoryBreakdown.slice(0, 5).map((c, i) => ({
      ...c,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }));
  }, [stats.categoryBreakdown]);

  const isInvalidDateRange = Boolean(fromDate && toDate && fromDate > toDate);

  if (isLoading) return <Loader message="Loading income dashboard..." />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {/* Page Header & Filters */}
      <PageHeader
        title="Income Dashboard"
        subtitle="Overview of school income sources, collection details, and financial analytics"
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={datePreset}
                label="Date Range"
                onChange={handlePresetChange}
              >
                {DATE_PRESET_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {datePreset === 'custom' && (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={fromDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFromDate(val);
                    if (toDate && val && val > toDate) {
                      toast.error('From Date cannot be later than To Date');
                    }
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { max: toDate || undefined },
                  }}
                  error={isInvalidDateRange}
                  helperText={isInvalidDateRange ? 'Must be ≤ To Date' : undefined}
                  sx={{ width: 145 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={toDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setToDate(val);
                    if (fromDate && val && val < fromDate) {
                      toast.error('To Date cannot be earlier than From Date');
                    }
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: fromDate || undefined },
                  }}
                  error={isInvalidDateRange}
                  helperText={isInvalidDateRange ? 'Must be ≥ From Date' : undefined}
                  sx={{ width: 145 }}
                />
              </>
            )}

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategoryId}
                label="Category"
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <MenuItem value="ALL">All Categories</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        }
      />

      {/* Row 1: Primary Stat Cards */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }} gap={1.75} sx={{ mb: 0 }}>
        <DashboardStatCard
          title="Total Income"
          value={formatCurrency(stats.total)}
          subtext={`${stats.count || 0} Collection Records`}
          subtextColor={tokens.colors.success.main}
          icon={Banknote}
          color="success"
          onClick={() => navigate('/income')}
        />
        <DashboardStatCard
          title="Avg Collection"
          value={formatCurrency(stats.avg)}
          subtext="Per transaction avg"
          subtextColor={tokens.colors.info.main}
          icon={TrendingUp}
          color="info"
          onClick={() => navigate('/income')}
        />
        <DashboardStatCard
          title="Largest Income"
          value={formatCurrency(stats.largest)}
          subtext="Highest single collection"
          subtextColor={tokens.colors.warning.main}
          icon={ArrowUpRight}
          color="warning"
          onClick={() => navigate('/income')}
        />
        <DashboardStatCard
          title="Active Categories"
          value={`${topCategories.length} / ${categories.length}`}
          subtext="Active income categories"
          subtextColor="#8B5CF6"
          icon={Tag}
          color="purple"
          onClick={() => navigate('/income/categories')}
        />
      </DashboardGrid>

      {/* Row 2: Top Categories Cards */}
      {topCategories.length > 0 && (
        <DashboardGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap={1.75} sx={{ mb: 0 }}>
          {topCategories.map((c) => (
            <DashboardStatCard
              key={c.category_id}
              title={c.category_name}
              value={formatCurrency(c.total_amount)}
              subtext={`${c.percentage_of_total}% of total (${c.expense_count} txns)`}
              subtextColor={c.color}
              icon={Tag}
              color={c.color}
              onClick={() => {
                setSelectedCategoryId(c.category_id);
              }}
            />
          ))}
        </DashboardGrid>
      )}

      {/* Row 3: Bar Chart + Donut Chart */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1.75 }}>
        <WidgetContainer
          title="Income by Category"
          action={
            <Button size="small" variant="outlined" onClick={() => navigate('/income/categories')}>
              View Categories
            </Button>
          }
        >
          <Box sx={{ width: '100%', height: 300 }}>
            {stats.categoryBreakdown.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={stats.categoryBreakdown} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.border} />
                  <XAxis
                    dataKey="category_name" tick={{ fontSize: 11, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false} interval={0}
                    angle={-25} textAnchor="end" height={45}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar
                    dataKey="total_amount" name="Total Amount"
                    fill={CHART_COLOR} radius={[4, 4, 0, 0]}
                    label={<BarLabel />} maxBarSize={45}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">No category breakdown data available</Typography>
              </Box>
            )}
          </Box>
        </WidgetContainer>

        <WidgetContainer
          title="Category Distribution"
          action={
            <Button size="small" sx={{ textTransform: 'none', color: tokens.colors.primary.main }} onClick={() => navigate('/income')}>
              View All
            </Button>
          }
        >
          <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%" cy="45%"
                    innerRadius={55} outerRadius={95}
                    paddingAngle={3} dataKey="value"
                    label={PieLabel} labelLine={false}
                  >
                    {pieChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle" iconSize={10}
                    formatter={(val) => {
                      const item = pieChartData.find((g) => g.name === val);
                      return (
                        <span style={{ fontSize: 11, color: tokens.colors.text.secondary }}>
                          {val}&nbsp;&nbsp;{formatCurrency(item?.value ?? 0)} ({item?.percentage ?? 0}%)
                        </span>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No distribution data available</Typography>
            )}
          </Box>
        </WidgetContainer>
      </Box>

      {/* Row 4: Recent Incomes + Daily Trend */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.45fr 1fr' }, gap: 1.75 }}>
        <WidgetContainer
          title="Recent Income Collections"
          noPadding
          action={
            <Button
              size="small"
              sx={{ textTransform: 'none', color: tokens.colors.primary.main }}
              onClick={() => navigate('/income')}
            >
              View All
            </Button>
          }
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={TABLE_SX}>
              <thead>
                <tr>
                  <th style={{ width: 28, textAlign: 'center' }}>#</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: tokens.colors.text.muted, py: 3 }}>
                      No recent income records
                    </td>
                  </tr>
                ) : (
                  stats.recent.map((inc, idx) => (
                    <tr key={inc.id || idx} onClick={() => navigate('/income')}>
                      <td style={{ width: 28, textAlign: 'center', color: tokens.colors.text.muted, fontSize: '0.75rem' }}>
                        {idx + 1}
                      </td>
                      <td>
                        <Typography variant="caption" sx={{ color: tokens.colors.text.secondary, whiteSpace: 'nowrap' }}>
                          {formatDate(inc.income_date)}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {inc.description || 'Income record'}
                        </Typography>
                      </td>
                      <td>
                        <Chip
                          label={inc.category_name}
                          size="small"
                          sx={{ fontSize: '0.7rem', fontWeight: 600, height: 20, bgcolor: tokens.colors.grey[100] }}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.colors.text.primary, whiteSpace: 'nowrap' }}>
                          {formatCurrency(inc.amount)}
                        </Typography>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Box>
          </Box>
        </WidgetContainer>

        <WidgetContainer title="Daily Income Collection Trend">
          <Box sx={{ width: '100%', height: 250 }}>
            {stats.dailyTrend.length > 0 ? (
              <ResponsiveContainer>
                <AreaChart data={stats.dailyTrend} margin={{ top: 10, right: 10, left: -15, bottom: 5 }}>
                  <defs>
                    <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLOR} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLOR} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.border} />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 10, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(val) => `₹${val}`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone" dataKey="total_amount" name="Total Collected"
                    stroke={CHART_COLOR} fillOpacity={1} fill="url(#incomeGrad)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">No trend data available for this range</Typography>
              </Box>
            )}
          </Box>
        </WidgetContainer>
      </Box>
    </Box>
  );
}
